import { useDispatch, useSelector } from 'react-redux'
import { useParams, NavLink, useNavigate } from 'react-router-dom'
import { RootState, AppDispatch } from '../../redux/store'
import { useEffect, useMemo, useState } from 'react'
import {
  fetchCriticalActionsByMissionId,
  fetchHighActionsByMissionId,
  fetchMediumActionsByMissionId,
  fetchLowActionsByMissionId,
} from '../../redux/missions/missionActions'
import LaunchIcon from '@mui/icons-material/Launch'
import DriveFileRenameOutlineOutlinedIcon from '@mui/icons-material/DriveFileRenameOutlineOutlined'
import DeleteButton from '../../components/common/DeleteButton'
import { deleteAction } from '../../redux/actions/actionActions'
import { hasRole } from '../../utils/checkRole'
import { UserRole } from '../../types/keycloak'
import { useTranslation } from 'react-i18next'
import UseAlert from '../../components/common/alert'
import {
  ListPageShell,
  listPageIconBtnStyle,
} from '../../components/common/ListPageShell'
import {
  ListPageDateCell,
  ListPagePill,
  PRIORITY_PILL,
  STATUS_PILL,
} from '../../components/common/ListPageCells'
import { ListPageDescription } from '../../components/common/ListPageDescription'
import { formatTitle } from '../../utils/formatTitle'
import { onClickableTableRow } from '../../utils/tableRowClick'
import { selectUsername } from '../../redux/keycloak/keycloakSelectors'
import { TypeActionTranslations } from '../../utils/constants'
import { formatCurrency } from '../../utils/formatNumbers'
import { selectSpecifications } from '../../redux/specification/specificationSelectors'
import i18n from '../../i18n'
import '../../components/common/ListPageTable.css'
import TableEmptyState from '../../components/common/TableEmptyState'
import TableLoadingState from '../../components/common/TableLoadingState'
import TablePager from '../../components/common/TablePager'
import { useClientPagination } from '../../hooks/useClientPagination'
import { useTableLoadingGate } from '../../hooks/useTableLoadingGate'
export function PriorityTasksByMissionId() {
  const { t } = useTranslation()
  const { type, missionId } = useParams<{ type: string; missionId: string }>()
  const dispatch = useDispatch<AppDispatch>()
  const navigate = useNavigate()
  const roles = useSelector((state: RootState) => state.auth.roles)
  const username = useSelector(selectUsername)
  const currency = useSelector(selectSpecifications)[0]?.Currency
  const loading = useSelector((state: RootState) => state.action.loading)
  const showLoader = useTableLoadingGate(loading, [type, missionId])

  const actionKey =
    type === 'Critical' || type === 'Critique'
      ? 'criticalActions'
      : type === 'High' || type === 'Élevé' || type === 'Élevée'
        ? 'highActions'
        : type === 'Medium' || type === 'Moyen' || type === 'Moyenne'
          ? 'meduimActions'
          : 'lowActions'

  const actions =
    useSelector((state: RootState) => {
      switch (type) {
        case 'Critical':
        case 'Critique':
          return state.mission.criticalActions
        case 'High':
        case 'Élevé':
        case 'Élevée':
          return state.mission.highActions
        case 'Medium':
        case 'Moyen':
        case 'Moyenne':
          return state.mission.mediumAction
        case 'Low':
        case 'Faible':
        case 'Basse':
          return state.mission.lowActions
        default:
          return []
      }
    }) || []

  const actionList = (actions?.[actionKey as keyof typeof actions] ||
    []) as any[]

  const refetch = () => {
    if (!missionId) return
    switch (type) {
      case 'Critical':
      case 'Critique':
        dispatch(fetchCriticalActionsByMissionId(missionId))
        break
      case 'High':
      case 'Élevé':
      case 'Élevée':
        dispatch(fetchHighActionsByMissionId(missionId))
        break
      case 'Medium':
      case 'Moyen':
      case 'Moyenne':
        dispatch(fetchMediumActionsByMissionId(missionId))
        break
      case 'Low':
      case 'Faible':
      case 'Basse':
        dispatch(fetchLowActionsByMissionId(missionId))
        break
      default:
        break
    }
  }

  useEffect(() => {
    refetch()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, missionId, dispatch])

  const [alert, setAlert] = useState({
    open: false,
    message: '',
    severity: '' as 'info' | 'success' | 'error' | 'warning',
  })
  const [search, setSearch] = useState('')

  const handleDeleteClick = async (ActionId: string) => {
    try {
      await dispatch(deleteAction(ActionId)).unwrap()
      setAlert({
        open: true,
        message: t('alerts.actionDelete.success'),
        severity: 'success',
      })
      refetch()
    } catch (error: any) {
      console.error('Error deleting action:', error.message)
      setAlert({
        open: true,
        message: t('alerts.actionDelete.error'),
        severity: 'error',
      })
    }
  }

  const priorityKey =
    type === 'Critical' || type === 'Critique'
      ? 'critical'
      : type === 'High' || type === 'Élevé' || type === 'Élevée'
        ? 'high'
        : type === 'Medium' || type === 'Moyen' || type === 'Moyenne'
          ? 'medium'
          : type === 'Low' || type === 'Faible' || type === 'Basse'
            ? 'low'
            : ''
  const translatedType = priorityKey
    ? t(`priorityTasksByMissionPage.taskTypes.${priorityKey}`)
    : ''

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return actionList
    return actionList.filter((a) =>
      [a.ActionName, a.Description, a.MissionName, a.Status]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q))
    )
  }, [actionList, search])

  const pager = useClientPagination(filtered, 10)

  const canDelete =
    hasRole(roles, UserRole.RSSI) ||
    hasRole(roles, UserRole.SSI_TEAM) ||
    hasRole(roles, UserRole.ADMIN)

  const canEditRow = (action: any) =>
    username?.toLocaleLowerCase() ===
      action?.CreatedBy?.toLocaleLowerCase() ||
    hasRole(roles, UserRole.RSSI) ||
    hasRole(roles, UserRole.ADMIN)

  return (
    <>
      <UseAlert
        open={alert.open}
        message={alert.message}
        severity={alert.severity}
        onClose={() => setAlert((prev) => ({ ...prev, open: false }))}
      />

      <ListPageShell
        eyebrow={t('priorityTasksByMissionPage.eyebrow')}
        title={`${translatedType} · ${t('priorityTasksByMissionPage.tasks')}`}
        subtitle={t('priorityTasksByMissionPage.subtitle')}
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder={t('filterByName')}
        countLabel={t('priorityTasksByMissionPage.count', {
          count: filtered.length,
        })}
      >
        <div className="incyia-table-wrap">
          {showLoader ? (
            <TableLoadingState />
          ) : filtered.length === 0 ? (
            <TableEmptyState />
          ) : (
            <>
            <table className="incyia-table">
              <thead>
                <tr>
                  <th>{t('task_name')}</th>
                  <th>{t('Description')}</th>
                  <th>{t('Start Date')}</th>
                  <th>{t('End Date')}</th>
                  <th>{t('Priority')}</th>
                  <th>{t('task_type')}</th>
                  <th>{t('Status')}</th>
                  <th className="incyia-num">{t('cost')}</th>
                  <th>{t('Mission Name')}</th>
                  <th style={{ textAlign: 'right' }}>{t('Actions')}</th>
                </tr>
              </thead>
              <tbody>
                {pager.pageItems.map((action) => {
                  const priority = Number(action.Priority)
                  const priorityStyle =
                    PRIORITY_PILL[priority] ?? PRIORITY_PILL[4]
                  const statusStyle = STATUS_PILL[action.Status || ''] ?? {
                    bg: '#EAF0F0',
                    color: '#5F7484',
                  }
                  const typeLabel =
                    TypeActionTranslations[action.TypeAction || ''] ||
                    action.TypeAction ||
                    '—'
                  const name = action.ActionName
                    ? formatTitle(action.ActionName)
                    : '—'

                  return (
                    <tr key={action._id} onClick={onClickableTableRow}>
                      <td className="incyia-col-pinned">
                        <NavLink
                          to={`/actiond/${action._id}`}
                          className="incyia-name-link"
                        >
                          <ListPageDescription
                            value={name}
                            tone="primary"
                          />
                        </NavLink>
                      </td>
                      <td className="incyia-wrap">
                        <ListPageDescription value={action.Description} />
                      </td>
                      <td>
                        <ListPageDateCell
                          value={action.DateDebut}
                          locale={i18n.language}
                        />
                      </td>
                      <td>
                        <ListPageDateCell
                          value={action.DateFin}
                          locale={i18n.language}
                        />
                      </td>
                      <td>
                        <ListPagePill
                          label={t(`priority.${priorityStyle.label}`, {
                            defaultValue: priorityStyle.label,
                          })}
                          bg={priorityStyle.bg}
                          color={priorityStyle.color}
                        />
                      </td>
                      <td>
                        <ListPageDescription value={typeLabel} />
                      </td>
                      <td>
                        <ListPagePill
                          label={action.Status || '—'}
                          bg={statusStyle.bg}
                          color={statusStyle.color}
                        />
                      </td>
                      <td>
                        <span className="incyia-money">
                          {formatCurrency(action.Cout ?? 0, currency)}
                        </span>
                      </td>
                      <td className="incyia-muted">
                        <ListPageDescription value={action.MissionName} />
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <div className="incyia-row-actions">
                          <button
                            type="button"
                            title={t('listPages.openAction')}
                            onClick={() => navigate(`/actiond/${action._id}`)}
                            style={listPageIconBtnStyle}
                          >
                            <LaunchIcon sx={{ fontSize: 15 }} />
                          </button>
                          {canEditRow(action) && (
                            <button
                              type="button"
                              title={t('listPages.edit')}
                              onClick={() =>
                                navigate(`/action/${action._id}`)
                              }
                              style={listPageIconBtnStyle}
                            >
                              <DriveFileRenameOutlineOutlinedIcon
                                sx={{ fontSize: 15 }}
                              />
                            </button>
                          )}
                          {canDelete && (
                            <DeleteButton
                              onDelete={() =>
                                handleDeleteClick(action._id as string)
                              }
                              item={{
                                entityType: t('tasks', {
                                  defaultValue: 'Task',
                                }),
                                name,
                                details: [
                                  {
                                    label: t('deleteConfirm.fields.status'),
                                    value: action.Status || '—',
                                  },
                                  {
                                    label: t('deleteConfirm.fields.priority'),
                                    value: priorityStyle.label,
                                  },
                                  {
                                    label: t('deleteConfirm.fields.mission'),
                                    value: action.MissionName || '—',
                                  },
                                ],
                              }}
                            />
                          )}
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
            {pager.showPager ? (
              <TablePager
                page={pager.page}
                pageSize={pager.pageSize}
                total={pager.total}
                pageCount={pager.pageCount}
                from={pager.from}
                to={pager.to}
                pageSizeOptions={pager.pageSizeOptions}
                onPageChange={pager.setPage}
                onPageSizeChange={pager.setPageSize}
              />
            ) : null}
            </>
          )}
        </div>
      </ListPageShell>
    </>
  )
}

export default PriorityTasksByMissionId
